import React, { useState } from 'react';

const LEVELS = [1, 2, 3, 4, 5, 6, 7, 8, 9];
const ORDINAL = { 1: '1st', 2: '2nd', 3: '3rd', 4: '4th', 5: '5th', 6: '6th', 7: '7th', 8: '8th', 9: '9th' };

export default function SpellSlotsCard({ playerRecord, updatePlayer, alwaysExpanded = false }) {
  const [expandedState, setExpanded] = useState(false);
  const expanded = alwaysExpanded || expandedState;
  const [editing,  setEditing]  = useState(false);
  const slots = playerRecord?.spellSlots || {};

  const setSlot = (lvl, patch) => {
    const current = slots[lvl] || { max: 0, used: 0 };
    const next    = { ...current, ...patch };
    next.max  = Math.max(0, next.max);
    next.used = Math.max(0, Math.min(next.max, next.used));
    updatePlayer(playerRecord.id, { spellSlots: { ...slots, [lvl]: next } });
  };

  const togglePip = (lvl, idx) => {
    const slot = slots[lvl] || { max: 0, used: 0 };
    // Pips fill left-to-right with "used". Tap a used pip to refresh, unused to consume.
    const consumedAtIdx = idx < slot.used;
    const nextUsed = consumedAtIdx ? idx : idx + 1;
    setSlot(lvl, { used: nextUsed });
  };

  const configuredLevels = LEVELS.filter((lvl) => (slots[lvl]?.max || 0) > 0);
  const visibleLevels    = editing ? LEVELS : configuredLevels;
  const hasAnySlots      = configuredLevels.length > 0;

  // Compact summary: total available / total max across all configured levels
  const totalRemaining = configuredLevels.reduce((acc, lvl) => acc + ((slots[lvl]?.max || 0) - (slots[lvl]?.used || 0)), 0);
  const totalMax       = configuredLevels.reduce((acc, lvl) => acc + (slots[lvl]?.max || 0), 0);

  return (
    <div style={styles.section}>
      {alwaysExpanded ? (
        <div style={styles.headerStatic}>
          <span style={styles.label}>Spell Slots</span>
          <span style={styles.summary}>
            {hasAnySlots ? `${totalRemaining}/${totalMax}` : 'none'}
          </span>
        </div>
      ) : (
        <button
          style={styles.headerBtn}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={() => setExpanded((v) => !v)}
        >
          <span style={styles.label}>Spell Slots</span>
          <span style={styles.summary}>
            {hasAnySlots ? `${totalRemaining}/${totalMax}` : 'none'}
          </span>
          <span style={styles.chevron}>{expanded ? '▾' : '▸'}</span>
        </button>
      )}

      {expanded && (
        <div style={styles.body}>
          <div style={styles.editRow}>
            <button
              style={styles.editBtn}
              onPointerDown={(e) => e.stopPropagation()}
              onClick={() => setEditing((v) => !v)}
              title={editing ? 'Done editing' : 'Edit slot maxes'}
            >
              {editing ? 'Done' : '✎ Edit'}
            </button>
          </div>

          {!hasAnySlots && !editing && (
            <div style={styles.emptyHint}>Tap ✎ Edit to configure your slots.</div>
          )}

          {editing ? (
            <div style={styles.editGrid}>
              {LEVELS.map((lvl) => {
                const slot = slots[lvl] || { max: 0, used: 0 };
                return (
                  <div key={lvl} style={styles.editCell}>
                    <span style={styles.levelLabel}>{ORDINAL[lvl]}</span>
                    <div style={styles.stepper}>
                      <button
                        style={styles.stepBtn}
                        onPointerDown={(e) => e.stopPropagation()}
                        onClick={() => setSlot(lvl, { max: slot.max - 1 })}
                        disabled={slot.max <= 0}
                      >−</button>
                      <span style={styles.stepValue}>{slot.max}</span>
                      <button
                        style={styles.stepBtn}
                        onPointerDown={(e) => e.stopPropagation()}
                        onClick={() => setSlot(lvl, { max: slot.max + 1 })}
                      >+</button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={styles.grid}>
              {configuredLevels.map((lvl) => {
                const slot = slots[lvl] || { max: 0, used: 0 };
                return (
                  <div key={lvl} style={styles.cell}>
                    <span style={styles.cellLabel}>{ORDINAL[lvl]}</span>
                    <span style={styles.cellCount}>{slot.max - slot.used}/{slot.max}</span>
                    <div style={styles.cellPips}>
                      {Array.from({ length: slot.max }).map((_, i) => {
                        const consumed = i < slot.used;
                        return (
                          <button
                            key={i}
                            style={{ ...styles.pip, ...(consumed ? styles.pipUsed : styles.pipFresh) }}
                            onPointerDown={(e) => e.stopPropagation()}
                            onClick={() => togglePip(lvl, i)}
                            title={consumed ? 'Used — tap to refresh' : 'Available — tap to consume'}
                          />
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const styles = {
  section:   { marginTop: 8, background: 'rgba(122,87,184,0.08)', border: '1px solid rgba(122,87,184,0.3)', borderRadius: 6, overflow: 'hidden' },
  headerBtn:    { width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left' },
  headerStatic: { display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px' },
  label:     { fontSize: 11, fontFamily: "'Press Start 2P', monospace", color: '#a987d4', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700, flex: 1 },
  summary:   { fontSize: 12, fontWeight: 800, color: '#c9b3e8' },
  chevron:   { fontSize: 10, color: '#a987d4', minWidth: 10 },
  body:      { padding: '4px 10px 8px', borderTop: '1px solid rgba(122,87,184,0.2)' },
  editRow:   { display: 'flex', justifyContent: 'flex-end', marginBottom: 4 },
  editBtn:   { fontSize: 10, fontFamily: "'Press Start 2P', monospace", color: '#a987d4', background: 'rgba(122,87,184,0.12)', border: '1px solid rgba(122,87,184,0.4)', borderRadius: 3, padding: '2px 8px', cursor: 'pointer', fontWeight: 700 },
  emptyHint: { fontSize: 11, color: '#7a6a92', textAlign: 'center', padding: '4px 0', fontStyle: 'italic' },
  grid:      { display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 4, padding: '2px 0' },
  cell:      { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, padding: '4px 2px', background: 'rgba(122,87,184,0.08)', border: '1px solid rgba(122,87,184,0.25)', borderRadius: 4, minHeight: 56 },
  cellEmpty: { background: 'rgba(122,87,184,0.03)', borderColor: 'rgba(122,87,184,0.12)' },
  cellLabel: { fontSize: 10, fontWeight: 700, color: '#c9b3e8', fontFamily: "'Press Start 2P', monospace", textTransform: 'uppercase', letterSpacing: '0.04em' },
  cellCount: { fontSize: 10, color: '#9a8b6e', fontWeight: 700 },
  cellPips:  { display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 3, marginTop: 2 },
  cellDash:  { fontSize: 12, color: '#4a3d5e', fontWeight: 700, marginTop: 4 },
  editGrid:  { display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 4, padding: '2px 0' },
  editCell:  { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, padding: '4px 2px', background: 'rgba(122,87,184,0.08)', border: '1px solid rgba(122,87,184,0.25)', borderRadius: 4 },
  levelLabel:{ fontSize: 10, fontWeight: 700, color: '#c9b3e8', fontFamily: "'Press Start 2P', monospace", textTransform: 'uppercase', letterSpacing: '0.04em' },
  stepper:   { display: 'flex', alignItems: 'center', gap: 2 },
  stepBtn:   { width: 18, height: 18, background: '#0e1528', border: '1px solid #4a3d28', borderRadius: 3, color: '#c9b3e8', fontSize: 12, fontWeight: 800, cursor: 'pointer', padding: 0, lineHeight: 1 },
  stepValue: { minWidth: 14, textAlign: 'center', fontSize: 12, fontWeight: 800, color: '#c9b3e8' },
  pip:       { width: 12, height: 12, borderRadius: '50%', cursor: 'pointer', padding: 0, transition: 'background 0.15s, border-color 0.15s' },
  pipFresh:  { background: '#a987d4', border: '1px solid #c9b3e8', boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.3)' },
  pipUsed:   { background: 'transparent', border: '1px solid #4a3d5e' },
};
